import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../context/AlertContext';
import { orderService } from '../../services/orderService';
import { useCustomers } from '../../context/CustomerContext';
import { ShoppingBag, Clock, XCircle, User, Hash, Utensils, Calendar, Truck, CreditCard, ChevronRight, Plus, UserPlus, RefreshCw, Calculator, X, Search, CheckCircle2, Package, MinusCircle } from 'lucide-react';
import ManualOrderModal from '../../components/ManualOrderModal';
import LinkCustomerModal from '../../components/LinkCustomerModal';
import SplitPaymentModal from '../../components/SplitPaymentModal';
import { anonymizeName } from '../../utils/customer';

const Orders = () => {
    const { t } = useTranslation();
    const { effectiveTenantId, tenantData, user, userData } = useAuth();
    const { showAlert, showConfirm } = useAlert();
    const { customers } = useCustomers();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
const [showManualOrderModal, setShowManualOrderModal] = useState(false);
    const [linkOrder, setLinkOrder] = useState(null);
    const [splitPaymentOrder, setSplitPaymentOrder] = useState(null);
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
    const [editOrderData, setEditOrderData] = useState(null);

    const activeOrderDetails = useMemo(() => {
        if (!selectedOrderDetails) return null;
        return orders.find(o => o.id === selectedOrderDetails.id) || selectedOrderDetails;
    }, [selectedOrderDetails, orders]);

    useEffect(() => {
        if (!effectiveTenantId) return;
        const unsubscribe = orderService.subscribeToOrders(effectiveTenantId, (orderData) => {
            setOrders(orderData);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [effectiveTenantId]);

    const [updatingOrderId, setUpdatingOrderId] = useState(null);
    const updateStatus = async (order, newStatus) => {
        if (updatingOrderId) return;
        setUpdatingOrderId(order.id);
        try {
            await orderService.updateOrderStatus(
                order,
                newStatus,
                effectiveTenantId,
                user.uid,
                userData?.email,
                tenantData
            );
        } catch (err) {
            console.error("Order Update Error:", err);
            showAlert("İşlem sırasında bir hata oluştu.", "error");
        } finally {
            setUpdatingOrderId(null);
        }
    };

    const filteredOrders = useMemo(() => {
        return orders.filter(o => {
            const statusMatch = filter === 'all' || o.status === filter;
            if (!statusMatch) return false;
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const ref = o.id?.slice(-6).toLowerCase();
            const table = getTableDisplayName(o.tableNumber).toLowerCase();
            const customer = getCustomerDisplayName(o).toLowerCase();
            const items = o.items?.map(i => i.name).join(' ').toLowerCase() || '';
            return ref?.includes(q) || table.includes(q) || customer.includes(q) || items.includes(q);
        });
    }, [orders, filter, searchQuery, customers]);

    const getCustomerDisplayName = (order) => {
        if (!order.customerId) return order.customerName || 'Misafir';
        const customer = customers.find(c => c.id === order.customerId);
        if (customer) {
            const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
            if (fullName) return anonymizeName(fullName);
        }
        return anonymizeName(order.customerName || 'Misafir');
    };

    const getTableDisplayName = (tableId) => {
        if (!tableId || tableId === 'Manuel') return 'Manuel Sipariş';
        if (tableId === 'Paket') return 'Paket Servis';
        if (tableId === 'GelAl') return 'Gel Al';
        if (String(tableId).toLowerCase() === 'desk') return 'Kasa';

        // Resolve from tenant tables if possible
        const table = tenantData?.tables?.find(t => String(t.id) === String(tableId));
        if (table) return table.name === 'desk' ? 'Kasa' : table.name;
        
        // Fallback to "Masa [ID]" if not found or already a name
        return String(tableId).startsWith('Masa') ? tableId : `Masa ${tableId}`;
    };

    const getStatusAesthetics = (status) => {
        switch (status) {
            case 'pending': return { color: '#f59e0b', bg: '#fef3c7', icon: Clock, label: 'Beklemede' };
            case 'delivered': return { color: '#3b82f6', bg: '#dbeafe', icon: Truck, label: 'Teslim Edildi' };
            case 'paid': return { color: '#10b981', bg: '#d1fae5', icon: CreditCard, label: 'Ödendi' };
            case 'cancelled': return { color: '#ef4444', bg: '#fef2f2', icon: XCircle, label: 'İptal Edildi' };
            default: return { color: '#6b7280', bg: '#f3f4f6', icon: Clock, label: status };
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
            <div className="loading-spinner"></div>
        </div>
    );

    return (
        <div className="fade-in">
            <header style={{ marginBottom: '2rem' }}>
                <h1 style={{ fontWeight: 900, fontSize: '2.25rem', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>{t('nav.orders')}</h1>
                <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Müşterilerinizden gelen anlık siparişleri buradan yönetebilirsiniz.</p>
            </header>

            <style>{`
                .orders-toolbar { display: flex; gap: 12px; align-items: center; margin-bottom: 1.5rem; flex-wrap: wrap; }
                .orders-toolbar .search-box { position: relative; flex: 1; min-width: 220px; max-width: 400px; }
                .orders-filter-tabs { display: flex; gap: 4px; background: rgba(var(--primary-rgb), 0.05); padding: 4px; border-radius: 12px; }
                .orders-filter-tabs button { padding: 8px 16px; border-radius: 10px; border: none; font-weight: 600; font-size: 0.8125rem; cursor: pointer; transition: all 0.2s; white-space: nowrap; }
                .orders-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1.5rem; margin-bottom: 2.5rem; }
                .orders-table-wrapper { overflow-x: auto; }
                .orders-table { width: 100%; border-collapse: collapse; }
                .orders-table th, .orders-table td { padding: 1rem; text-align: left; }
                .orders-table th { color: var(--text-muted); font-size: 0.75rem; text-transform: uppercase; }
                .orders-create-btn .full-text { display: inline; }
                .orders-create-btn .short-text { display: none; }

                @media (max-width: 768px) {
                    .orders-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 0.75rem !important; }
                    .orders-stats-grid .card { padding: 1rem !important; }
                    .orders-stats-grid .stat-icon { padding: 8px !important; }
                    .orders-stats-grid .stat-icon svg { width: 18px !important; height: 18px !important; }
                    .orders-stats-grid .stat-value { font-size: 1.5rem !important; }
                    .orders-stats-grid .stat-label { font-size: 0.6875rem !important; }
                    .orders-stats-grid .stat-sub { font-size: 0.625rem !important; }

                    .orders-toolbar { flex-direction: column; gap: 8px; }
                    .orders-toolbar .search-box { max-width: none !important; min-width: 0 !important; width: 100%; }
                    .orders-toolbar .toolbar-actions { width: 100%; display: flex; gap: 8px; }
                    .orders-filter-tabs { flex: 1; overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
                    .orders-filter-tabs::-webkit-scrollbar { display: none; }
                    .orders-filter-tabs button { padding: 8px 10px !important; font-size: 0.75rem !important; }
                    .orders-create-btn { padding: 0 14px !important; height: 38px !important; flex-shrink: 0; }
                    .orders-create-btn .full-text { display: none !important; }
                    .orders-create-btn .short-text { display: inline !important; }

                    .orders-table .col-ref, .orders-table .col-date { display: none; }
                    .orders-table th, .orders-table td { padding: 0.75rem 0.5rem !important; font-size: 0.8125rem; }
                    .orders-table .status-chip { padding: 4px 8px !important; font-size: 0.625rem !important; }
                    .orders-table .status-chip .status-label { display: none; }
                    .orders-table .quick-action-btn { padding: 5px 10px !important; font-size: 0.6875rem !important; }
                    .orders-table .quick-action-btn span { display: none; }
                    .orders-table .items-text { max-width: 120px !important; }
                }

                @media (max-width: 480px) {
                    .orders-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
                    .orders-stats-grid .stat-active-badge { display: none !important; }
                    .orders-filter-tabs button { padding: 6px 8px !important; font-size: 0.6875rem !important; }
                }
            `}</style>

            {/* Stats */}
            {(() => {
                const pendingOrders = orders.filter(o => o.status === 'pending');
                const deliveredOrders = orders.filter(o => o.status === 'delivered');
                const paidOrders = orders.filter(o => o.status === 'paid');
                const today = new Date().toDateString();
                const todayCount = orders.filter(o => {
                    const d = o.timestamp?.toDate ? o.timestamp.toDate() : new Date(o.timestamp);
                    return d.toDateString() === today;
                }).length;
                const pendingTotal = pendingOrders.reduce((s, o) => s + (o.totalAmount || 0), 0);
                const deliveredTotal = deliveredOrders.reduce((s, o) => s + (o.remainingAmount ?? o.totalAmount ?? 0), 0);
                const paidTotal = paidOrders.reduce((s, o) => s + (o.totalAmount || 0), 0);

                const stats = [
                    { key: 'pending', label: 'Bekleyenler', value: pendingOrders.length, subValue: `₺${pendingTotal.toFixed(0)} bekliyor`, color: '#f59e0b', icon: Clock },
                    { key: 'delivered', label: 'Teslim Edilenler', value: deliveredOrders.length, subValue: `₺${deliveredTotal.toFixed(0)} ödeme bekliyor`, color: '#3b82f6', icon: Truck },
                    { key: 'paid', label: 'Ödenenler', value: paidOrders.length, subValue: `₺${paidTotal.toFixed(0)} ciro`, color: '#10b981', icon: CreditCard },
                    { key: 'all', label: 'Toplam Sipariş', value: orders.length, subValue: `Bugün: ${todayCount}`, color: 'var(--primary)', icon: ShoppingBag },
                ];

                return (
                    <div className="orders-stats-grid">
                        {stats.map(stat => (
                            <div
                                key={stat.key}
                                className="card glass-premium clickable"
                                onClick={() => setFilter(stat.key)}
                                style={{
                                    padding: '1.5rem',
                                    borderBottom: `4px solid ${stat.color}${filter === stat.key ? '' : '40'}`,
                                    transition: 'all 0.2s',
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                    <div className="stat-icon" style={{ background: `${stat.color}15`, color: stat.color, padding: '10px', borderRadius: '12px' }}>
                                        <stat.icon size={22} />
                                    </div>
                                    {filter === stat.key && (
                                        <span className="stat-active-badge" style={{ fontSize: '0.625rem', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', background: `${stat.color}15`, color: stat.color, textTransform: 'uppercase' }}>Aktif</span>
                                    )}
                                </div>
                                <div className="stat-label" style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{stat.label}</div>
                                <div className="stat-value" style={{ fontSize: '1.75rem', fontWeight: 900, marginBottom: '2px' }}>{stat.value}</div>
                                <div className="stat-sub" style={{ fontSize: '0.75rem', fontWeight: 600, color: stat.color }}>{stat.subValue}</div>
                            </div>
                        ))}
                    </div>
                );
            })()}

            {/* Search + Filter Toolbar */}
            <div className="orders-toolbar">
                <div className="search-box" style={{ position: 'relative', flex: 1, minWidth: '220px', maxWidth: '400px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Sipariş, masa veya müşteri ara..."
                        style={{ paddingLeft: '40px', width: '100%', height: '42px', background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: '12px', outline: 'none', fontSize: '0.875rem' }}
                    />
                </div>
                <div className="toolbar-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div className="orders-filter-tabs">
                        {[
                            { id: 'all', label: 'Tümü' },
                            { id: 'pending', label: 'Beklemede' },
                            { id: 'delivered', label: 'Teslim' },
                            { id: 'paid', label: 'Ödendi' },
                            { id: 'cancelled', label: 'İptal' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setFilter(tab.id)}
                                style={{
                                    background: filter === tab.id ? 'var(--primary)' : 'transparent',
                                    color: filter === tab.id ? 'var(--text-on-primary)' : 'var(--text-muted)',
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <button
                        className="orders-create-btn btn-primary"
                        onClick={() => setShowManualOrderModal(true)}
                        style={{
                            height: '42px', padding: '0 20px', borderRadius: '12px',
                            border: 'none', fontWeight: 700, display: 'flex',
                            alignItems: 'center', gap: '8px', cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                    >
                        <Plus size={18} />
                        <span className="full-text">Manuel Sipariş Oluştur</span>
                        <span className="short-text">Sipariş</span>
                    </button>
                </div>
            </div>

            {/* Orders Table */}
            <div className="card glass" style={{ padding: '1.5rem' }}>
                <div className="orders-table-wrapper">
                    <table className="orders-table">
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                <th className="col-ref">Sipariş Ref</th>
                                <th>Masa / Müşteri</th>
                                <th>İçerik</th>
                                <th>Tutar</th>
                                <th className="col-date">Tarih / Zaman</th>
                                <th>Durum</th>
                                <th style={{ textAlign: 'right' }}>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                            <div style={{ width: '64px', height: '64px', background: 'rgba(var(--primary-rgb), 0.08)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <ShoppingBag size={32} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
                                            </div>
                                            <div>
                                                <h3 style={{ fontWeight: 700, fontSize: '1.125rem', marginBottom: '0.25rem' }}>
                                                    {searchQuery ? 'Arama sonucu bulunamadı' : filter === 'pending' ? 'Bekleyen sipariş yok' : filter === 'delivered' ? 'Teslim edilmiş sipariş yok' : filter === 'paid' ? 'Ödenmiş sipariş yok' : 'Henüz sipariş yok'}
                                                </h3>
                                                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>
                                                    {searchQuery ? 'Farklı bir arama terimi deneyin.' : 'Yeni siparişler burada görünecek.'}
                                                </p>
                                            </div>
                                            {!searchQuery && orders.length === 0 && (
                                                <button
                                                    onClick={() => setShowManualOrderModal(true)}
                                                    style={{ marginTop: '0.5rem', padding: '10px 24px', borderRadius: '12px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                                                >
                                                    <Plus size={18} /> Manuel Sipariş Oluştur
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredOrders.map((order) => {
                                    const aesthetics = getStatusAesthetics(order.status);
                                    const displayDate = (order.status === 'paid' && order.paidAt) ? order.paidAt : order.timestamp;
                                    const date = displayDate?.toDate ? displayDate.toDate() : new Date(displayDate);

                                    return (
                                        <React.Fragment key={order.id}>
                                            <tr
                                                onClick={() => setSelectedOrderDetails(order)}
                                                style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s', cursor: 'pointer' }}
                                                className="hover-bg"
                                            >
                                                <td className="col-ref" style={{ padding: '1rem' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <Hash size={14} color="var(--text-muted)" />
                                                        <span style={{ fontWeight: 700, fontSize: '0.875rem' }}>{order.id.slice(-6).toUpperCase()}</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <span style={{ fontWeight: 800, fontSize: '0.9375rem', color: 'var(--primary)' }}>
                                                                {getTableDisplayName(order.tableNumber)}
                                                            </span>
                                                            {order.tableNumber === 'Paket' && (
                                                                <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 6px', borderRadius: '6px', background: '#dbeafe', color: '#2563eb' }}>Paket</span>
                                                            )}
                                                            {order.tableNumber === 'GelAl' && (
                                                                <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 6px', borderRadius: '6px', background: '#dcfce7', color: '#16a34a' }}>Gel Al</span>
                                                            )}
                                                            {order.tableNumber === 'Manuel' && (
                                                                <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '2px 6px', borderRadius: '6px', background: '#ffedd5', color: '#c2410c' }}>Manuel</span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                                                            <User size={12} /> {getCustomerDisplayName(order)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem', maxWidth: '240px' }}>
                                                    {(() => {
                                                        const itemsText = order.items.map(i => `${i.quantity}x ${i.name}`).join(', ');
                                                        const totalItems = order.items.reduce((s, i) => s + i.quantity, 0);
                                                        return (
                                                            <div>
                                                                <div className="items-text" style={{ fontSize: '0.8125rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '220px' }} title={itemsText}>
                                                                    {itemsText}
                                                                </div>
                                                                {totalItems > 1 && (
                                                                    <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', background: 'var(--bg-main)', padding: '2px 8px', borderRadius: '6px', marginTop: '4px', display: 'inline-block' }}>
                                                                        {totalItems} ürün
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div style={{ fontWeight: 800 }}>
                                                        {order.status !== 'paid' && order.remainingAmount != null && order.remainingAmount < order.totalAmount ? (
                                                            <>
                                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textDecoration: 'line-through' }}>₺{order.totalAmount}</div>
                                                                <div style={{ color: '#f59e0b' }}>₺{Number(order.remainingAmount).toFixed(2)} kalan</div>
                                                            </>
                                                        ) : (
                                                            `₺ ${order.totalAmount}`
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="col-date" style={{ padding: '1rem' }}>
                                                    <div style={{ fontSize: '0.8125rem', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <Calendar size={12} /> {date.toLocaleDateString('tr-TR')}
                                                        </div>
                                                        <div style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <Clock size={12} /> {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem' }}>
                                                    <div
                                                        style={{
                                                            background: aesthetics.bg,
                                                            color: aesthetics.color,
                                                            padding: '6px 14px',
                                                            borderRadius: '100px',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 800,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <aesthetics.icon size={12} />
                                                        <span className="status-label">{aesthetics.label}</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                                                        {order.status === 'pending' && (
                                                            <>
                                                                <button
                                                                    className="quick-action-btn"
                                                                    onClick={() => updateStatus(order, 'delivered')}
                                                                    style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
                                                                >
                                                                    <Truck size={13} /> <span>Teslim Et</span>
                                                                </button>
                                                                <button
                                                                    className="quick-action-btn"
                                                                    onClick={() => { showConfirm('Bu siparişi iptal etmek istediğinize emin misiniz?', { title: 'Sipariş İptal', confirmText: 'İptal Et', type: 'error' }).then(ok => ok && updateStatus(order, 'cancelled')); }}
                                                                    style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#fef2f2', color: '#ef4444', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
                                                                >
                                                                    <MinusCircle size={13} /> <span>İptal</span>
                                                                </button>
                                                            </>
                                                        )}
                                                        {order.status === 'delivered' && (
                                                            <>
                                                                <button
                                                                    className="quick-action-btn"
                                                                    onClick={() => setSelectedOrderDetails(order)}
                                                                    style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#10b981', color: 'white', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
                                                                >
                                                                    <CreditCard size={13} /> <span>Ödeme Al</span>
                                                                </button>
                                                                <button
                                                                    className="quick-action-btn"
                                                                    onClick={() => { showConfirm('Bu siparişi iptal etmek istediğinize emin misiniz?', { title: 'Sipariş İptal', confirmText: 'İptal Et', type: 'error' }).then(ok => ok && updateStatus(order, 'cancelled')); }}
                                                                    style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#fef2f2', color: '#ef4444', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
                                                                >
                                                                    <MinusCircle size={13} /> <span>İptal</span>
                                                                </button>
                                                            </>
                                                        )}
                                                        {order.status === 'paid' && (
                                                            <CheckCircle2 size={18} style={{ color: '#10b981' }} />
                                                        )}
                                                        {order.status === 'cancelled' && (
                                                            <MinusCircle size={18} style={{ color: '#ef4444', opacity: 0.5 }} />
                                                        )}
                                                        <ChevronRight size={18} style={{ color: 'var(--text-muted)', opacity: 0.4, cursor: 'pointer' }} onClick={() => setSelectedOrderDetails(order)} />
                                                    </div>
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {(showManualOrderModal || editOrderData) && (
                <ManualOrderModal
                    initialOrder={editOrderData}
                    onClose={() => {
                        setShowManualOrderModal(false);
                        setEditOrderData(null);
                        if (editOrderData) {
                            setSelectedOrderDetails(null);
                        }
                    }}
                    onOrderCreated={() => {
                        // Refresh logic if needed, usually onSnapshot handles it
                    }}
                />
            )}

            {linkOrder && (
                <LinkCustomerModal
                    order={linkOrder}
                    onClose={() => setLinkOrder(null)}
                    onLinked={(customer) => {
                        // If null, user chose "Anonim Devam Et"
                        if (customer === null) {
                            updateStatus(linkOrder, 'paid');
                        }
                        // If a customer was selected, the modal already updated Firestore.
                        // We just close the modal and let the user click "Ödemeyi Onayla" 
                        // once the status/customer updates in the UI.
                        setLinkOrder(null);
                    }}
                />
            )}

            {splitPaymentOrder && (
                <SplitPaymentModal
                    order={orders.find(o => o.id === splitPaymentOrder.id) || splitPaymentOrder}
                    tenantData={tenantData}
                    onClose={() => setSplitPaymentOrder(null)}
                    onPaymentSuccess={(isDone) => {
                        // Only close the modal if the order is fully paid
                        if (isDone) {
                            setSplitPaymentOrder(null);
                            setSelectedOrderDetails(null);
                        }
                    }}
                />
            )}

            {/* Order Details Side Panel */}
            {activeOrderDetails && (
                <>
                    <style>{`
                        @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                    `}</style>
                    <div onClick={() => setSelectedOrderDetails(null)} style={{
                        position: 'fixed', inset: 0, zIndex: 1000,
                        background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '1rem',
                        animation: 'fadeIn 0.2s ease-out'
                    }}>
                    <div onClick={e => e.stopPropagation()} className="card" style={{
                        width: '100%', maxWidth: '440px', maxHeight: '90vh',
                        borderRadius: '24px',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        background: 'var(--bg-main)',
                        boxShadow: '0 25px 60px rgba(0, 0, 0, 0.2)',
                        animation: 'scaleIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 1001,
                    }}>

                        {/* Panel Header */}
                        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)', flexShrink: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(var(--primary-rgb), 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                        <Hash size={16} />
                                    </div>
                                    <h2 style={{ fontSize: '1.25rem', fontWeight: 800 }}>{activeOrderDetails.id.slice(-6).toUpperCase()}</h2>
                                    {(() => {
                                        const s = getStatusAesthetics(activeOrderDetails.status);
                                        return (
                                            <span style={{ fontSize: '0.6875rem', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', background: s.bg, color: s.color, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <s.icon size={11} /> {s.label}
                                            </span>
                                        );
                                    })()}
                                </div>
                                <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 600, paddingLeft: '40px' }}>
                                    {getTableDisplayName(activeOrderDetails.tableNumber)} • {getCustomerDisplayName(activeOrderDetails)}
                                </span>
                            </div>
                            <button
                                onClick={() => setSelectedOrderDetails(null)}
                                style={{ background: 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-main)' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Panel Body */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Timeline */}
                            <div>
                                <h4 style={{ fontWeight: 800, fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Zaman Çizelgesi</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {[
                                        { label: 'Oluşturuldu', time: activeOrderDetails.timestamp, icon: Clock, color: '#f59e0b', always: true },
                                        { label: 'Teslim Edildi', time: activeOrderDetails.status === 'delivered' || activeOrderDetails.status === 'paid' ? activeOrderDetails.updatedAt : null, icon: Truck, color: '#3b82f6', always: false },
                                        { label: 'Ödendi', time: activeOrderDetails.paidAt, icon: CreditCard, color: '#10b981', always: false },
                                    ].filter(step => step.always || step.time).map((step, idx) => {
                                        const date = step.time ? (step.time.toDate ? step.time.toDate() : new Date(step.time)) : null;
                                        return (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: date ? `${step.color}08` : 'var(--bg-card)', borderRadius: '10px', border: `1px solid ${date ? step.color + '20' : 'var(--border)'}` }}>
                                                <step.icon size={14} style={{ color: date ? step.color : 'var(--text-muted)', opacity: date ? 1 : 0.4 }} />
                                                <span style={{ fontSize: '0.8125rem', fontWeight: 600, flex: 1 }}>{step.label}</span>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                    {date ? `${date.toLocaleDateString('tr-TR')} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '—'}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Items */}
                            <div>
                                <h4 style={{ fontWeight: 800, fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Sipariş İçeriği</h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {activeOrderDetails.items.map((item, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ background: 'var(--primary)', color: 'white', fontWeight: 800, width: '28px', height: '28px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8125rem' }}>{item.quantity}</div>
                                                <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{item.name}</span>
                                            </div>
                                            <span style={{ fontWeight: 800, color: 'var(--text-main)', fontSize: '0.95rem' }}>₺{(item.price * item.quantity).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Payment history if exists */}
                            {activeOrderDetails.payments?.length > 0 && (
                                <div>
                                    <h4 style={{ fontWeight: 800, fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>Ödeme Geçmişi</h4>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        {activeOrderDetails.payments.map((p, idx) => (
                                            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#10b98108', borderRadius: '10px', border: '1px solid #10b98120', fontSize: '0.8125rem' }}>
                                                <span style={{ fontWeight: 600 }}>{p.customerName || 'Misafir'}</span>
                                                <span style={{ fontWeight: 800, color: '#10b981' }}>₺{Number(p.amount).toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Panel Footer / Actions */}
                        <div style={{ padding: '1.5rem', borderTop: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', flexDirection: 'column', gap: '1rem', flexShrink: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-muted)' }}>Toplam Tutar</span>
                                <span style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--primary)' }}>
                                    {activeOrderDetails.status !== 'paid' && activeOrderDetails.remainingAmount != null && activeOrderDetails.remainingAmount < activeOrderDetails.totalAmount ? (
                                        <>
                                            <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textDecoration: 'line-through', marginRight: '8px' }}>₺{activeOrderDetails.totalAmount}</span>
                                            <span style={{ color: '#f59e0b' }}>₺{Number(activeOrderDetails.remainingAmount).toFixed(2)} kalan</span>
                                        </>
                                    ) : (
                                        `₺${Number(activeOrderDetails.totalAmount || 0).toFixed(2)}`
                                    )}
                                </span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {activeOrderDetails.status === 'pending' && (
                                    <>
                                        <button
                                            onClick={() => { updateStatus(activeOrderDetails, 'delivered'); setSelectedOrderDetails(null); }}
                                            style={{ padding: '14px', borderRadius: '14px', border: 'none', background: '#3b82f6', color: 'white', fontWeight: 800, fontSize: '0.9375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 6px 16px rgba(59, 130, 246, 0.3)', width: '100%' }}
                                        >
                                            <Truck size={18} /> Teslim Et
                                        </button>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button
                                                onClick={() => { setEditOrderData(activeOrderDetails); setSelectedOrderDetails(null); }}
                                                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '2px dashed var(--border)', background: 'transparent', color: 'var(--text-main)', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                            >
                                                <Utensils size={16} /> Düzenle
                                            </button>
                                            <button
                                                onClick={() => {
                                                    showConfirm("Bu siparişi iptal etmek istediğinize emin misiniz?", { title: 'Sipariş İptal', confirmText: 'İptal Et', type: 'error' }).then(ok => { if (ok) {
                                                        updateStatus(activeOrderDetails, 'cancelled');
                                                        setSelectedOrderDetails(null);
                                                    }});
                                                }}
                                                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #ef4444', background: 'rgba(239, 68, 68, 0.05)', color: '#ef4444', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                                            >
                                                <XCircle size={16} /> İptal Et
                                            </button>
                                        </div>
                                    </>
                                )}
                                {activeOrderDetails.status === 'delivered' && (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            onClick={() => {
                                                if (!activeOrderDetails.customerId) setLinkOrder(activeOrderDetails);
                                                else { updateStatus(activeOrderDetails, 'paid'); setSelectedOrderDetails(null); }
                                            }}
                                            style={{ flex: 1, padding: '14px', borderRadius: '14px', border: 'none', background: '#10b981', color: 'white', fontWeight: 800, fontSize: '0.9375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 6px 16px rgba(16, 185, 129, 0.3)' }}
                                        >
                                            <CreditCard size={18} /> Ödeme Al
                                        </button>
                                        <button
                                            onClick={() => { setSplitPaymentOrder(activeOrderDetails); }}
                                            style={{ flex: 1, padding: '14px', borderRadius: '14px', border: '1px solid var(--primary)', background: 'rgba(var(--primary-rgb), 0.05)', color: 'var(--primary)', fontWeight: 800, fontSize: '0.9375rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                        >
                                            <Calculator size={18} /> Böl / Öde
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Orders;
